import React from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Auth } from 'aws-amplify';
import { useQueryClient } from '@tanstack/react-query';
import { ROUTES } from '../constants';
import { useAuth } from '../hooks/useAuth';
import { useSalesDashboardAccess } from '../hooks/sales/useSalesDashboardAccess';

interface SalesDashboardRouteProps {
  children: React.ReactNode;
}

const getErrorStatus = (error: unknown): number | null => {
  if (
    typeof error === 'object' &&
    error !== null &&
    'response' in error &&
    typeof error.response === 'object' &&
    error.response !== null &&
    'status' in error.response &&
    typeof error.response.status === 'number'
  ) {
    return error.response.status;
  }

  return null;
};

const FullScreenMessage = ({
  title,
  description,
  action,
}: {
  title: string;
  description: string;
  action?: React.ReactNode;
}) => (
  <main className="flex min-h-screen items-center justify-center bg-[#F6F8FC] px-4 py-10 text-[#0F172A]">
    <div className="w-full max-w-lg rounded-[28px] border border-slate-200 bg-white p-8 text-center shadow-[0_20px_60px_rgba(15,23,42,0.08)]">
      <h1 className="text-[28px] font-semibold tracking-[-0.03em] text-slate-900">
        {title}
      </h1>
      <p className="mt-3 text-sm leading-6 text-slate-500 sm:text-base">
        {description}
      </p>
      {action ? <div className="mt-6 flex justify-center">{action}</div> : null}
    </div>
  </main>
);

FullScreenMessage.defaultProps = {
  action: null,
};

const SalesDashboardRoute: React.FC<SalesDashboardRouteProps> = ({
  children,
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const auth = useAuth();
  const { clearUser } = auth as { clearUser?: () => void };
  const isLoadingAuth = (auth as { isLoading?: boolean }).isLoading || false;
  const isAuthenticated =
    (auth as { isAuthenticated?: boolean }).isAuthenticated || false;

  const {
    data: access,
    isLoading: isLoadingAccess,
    isError,
    error,
  } = useSalesDashboardAccess();

  const handleSignOut = async () => {
    try {
      clearUser?.();
      queryClient.setQueryData(['auth', 'currentUser'], null);
      await Auth.signOut();
    } catch (_error) {
      // Ignore sign-out cleanup errors and continue redirect.
    }

    navigate(ROUTES.LOGIN, { replace: true });
  };

  if (isLoadingAuth || (isAuthenticated && isLoadingAccess)) {
    return (
      <FullScreenMessage
        title="Checking access"
        description="Verifying your Sales Dashboard access."
      />
    );
  }

  if (!isAuthenticated) {
    return <Navigate to={ROUTES.LOGIN} replace />;
  }

  const status = getErrorStatus(error);
  if ((isError && status === 403) || access?.canAccess === false) {
    return (
      <FullScreenMessage
        title="Access denied"
        description="You do not have access to Sales Dashboard."
        action={
          <button
            type="button"
            onClick={handleSignOut}
            className="inline-flex items-center justify-center rounded-2xl bg-slate-900 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800"
          >
            Sign out
          </button>
        }
      />
    );
  }

  if (isError) {
    return (
      <FullScreenMessage
        title="Unable to load access"
        description="The access check failed. Try again in a moment."
      />
    );
  }

  return <>{children}</>;
};

export default SalesDashboardRoute;
